<template>
  <div>
    <HgCardElevated :class="$style.elevatedCard">
      <div :class="$style.portalContentWrapper">
        <span :class="$style.title">{{ portalDataObject.title }}</span>
        <img :src="imgUrl" :alt="$t('localLanding.sellingPortal.altImg')" loading="lazy" />
        <div :class="$style.contentContainer">
          <div
            v-for="item in portalDataObject.advantages"
            :key="item.heading"
            :class="$style.contentBox"
            data-name="contentBox"
          >
            <CheckIcon :class="$style.icon" />
            <div :class="$style.content">
              <span :class="$style.heading">{{ item.heading }}</span>
              <p :class="$style.contentText">{{ item.contentText }}</p>
            </div>
          </div>
        </div>
        <HgButton
          theme="secondary-bordered"
          :tag="isDIY ? 'a' : 'button'"
          :href="diyHref"
          target="_blank"
          @click="onButtonClick(portalDataObject.id)"
          >{{ portalDataObject.buttonText }}</HgButton
        >
      </div>
    </HgCardElevated>
    <InformedContactModal
      ref="contactModalRef"
      :from="CallMeBackModalFrom.AgentSell"
      :modalType="ContactModalType.AGENCY_RECOMMENDATION"
    />
  </div>
</template>

<script setup lang="ts">
import CheckIcon from '@/assets/icons/check.svg?component';
import agentImageHg from '@/assets/images/local-landing-selling-portal/agent_image_hg.svg?url';
import agentImageIs24 from '@/assets/images/local-landing-selling-portal/agent_image_is24.svg?url';
import privateImageHg from '@/assets/images/local-landing-selling-portal/private_image_hg.svg?url';
import privateImageIs24 from '@/assets/images/local-landing-selling-portal/private_image_is24.svg?url';
import { HgButton, HgCardElevated } from '@smg-real-estate/domus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { InformedContactModal } from '@/components/common';
import { getCurrentPlatform } from '@/helpers/Platform/Platform';
import { trackingGenericClickEvent } from '@/tracking/tracking';
import {
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { ContactModalFrom, ContactModalType, SellingTypes } from '@/types';

interface AdvantageDataObject {
  heading: string | undefined;
  contentText: string | undefined;
}

export interface PortalDataObject {
  id: SellingTypes;
  title: string | undefined;
  advantages: Array<AdvantageDataObject>;
  buttonText: string | undefined;
}

// Props
const props = defineProps<{
  portalDataObject: PortalDataObject;
}>();

// Composables
const { t, locale } = useI18n();

// Reactive data
const contactModalRef = ref<InstanceType<typeof InformedContactModal> | null>(null);

// Constants
const CallMeBackModalFrom = ContactModalFrom;
const imageUrls: Record<string, string> = {
  [`${SellingTypes.AGENT}_hg`]: agentImageHg,
  [`${SellingTypes.AGENT}_is24`]: agentImageIs24,
  [`${SellingTypes.DIY}_hg`]: privateImageHg,
  [`${SellingTypes.DIY}_is24`]: privateImageIs24,
};

// Computed properties
const isDIY = computed(() => props.portalDataObject.id === SellingTypes.DIY);
const imgUrl = computed(
  () => imageUrls[`${props.portalDataObject.id}_${getCurrentPlatform()}`] ?? privateImageHg,
);

const diyHref = computed(() => {
  return isDIY.value
    ? `${import.meta.env.VUE_APP_BASE_URL}/${locale.value}/insertion/${t('localLanding.sellingPortal.urlPath')}`
    : undefined;
});

// Methods
const onButtonClick = (id: string) => {
  const action =
    id === SellingTypes.DIY
      ? LOCAL_LANDING_PAGE_ACTION.INSERT_AD
      : LOCAL_LANDING_PAGE_ACTION.REQUEST_CALLBACK;

  trackingGenericClickEvent({
    event: LOCAL_LANDING_PAGE_EVENT.BUTTON_CLICK,
    category: LOCAL_LANDING_PAGE_CATEGORY.BUTTON_CLICK,
    action: action,
    label: `${id}_sell_cta`,
  });

  if (id !== SellingTypes.DIY) {
    contactModalRef.value?.open();
  }
};
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

$icon-size: $space-150;

.portalContentWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  align-items: center;
  height: auto;
  padding: $space-100 $space-100 $space-200 $space-100;
  color: $color-grey-100;

  .contentContainer {
    display: flex;
    flex-direction: column;
    gap: $space-75;
    padding-bottom: $space-75;

    .contentBox {
      display: flex;
      gap: $space-75;

      .content {
        flex: 1;
      }

      .heading {
        @include body-3-bold;
      }

      .contentText {
        @include body-3;
        @include vertical-margin;
      }
    }
  }

  .title {
    @include heading-4;
  }
}

.elevatedCard {
  [class*='HgCardElevated_content'] {
    box-shadow: $elevation-medium !important;
  }
}

.icon {
  width: $icon-size;
  height: $icon-size;
}
</style>
